import { Controller, useFormContext } from "react-hook-form";

import {
  CloudSettings,
  LogoAwsColored,
  LogoAzureColored,
  LogoGcpColored,
} from "components/icons/generated";
import TileCheckboxGrid from "components/TileCheckboxGrid";
import Box from "ds/components/Box";
import ComboBox from "ds/components/ComboBox";
import ComboBoxDefaultItem from "ds/components/ComboBox/DefaultItem";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import FormField from "ds/components/Form/Field";
import Select from "ds/components/Select";

import { SCHEDULE_SCAN_WIZARD_STEP_HEADERS } from "../../constants";
import useAttachableAwsIntegrations from "../../hooks/useAttachableAwsIntegrations";
import useScanProviderSchema from "../../hooks/useScanProviderSchema";
import { NewScanScheduleFormFields, NewScanScheduleStep } from "../../types";
import ProviderTile, { ProviderTileProps } from "./ProviderTile";
import RegionsField from "./RegionsField";
import tooltips from "../tooltips";

const PROVIDERS: ProviderTileProps[] = [
  { name: "AWS", icon: LogoAwsColored, selected: true },
  { name: "Azure", icon: LogoAzureColored, comingSoon: true },
  { name: "GCP", icon: LogoGcpColored, comingSoon: true },
  { name: "Custom", icon: CloudSettings, comingSoon: true },
];

const NewScanScheduleStepAttachProviders = () => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<NewScanScheduleFormFields>();

  const space = watch("space");

  const {
    providerVersions,
    regions,
    loading: schemaLoading,
    error: schemaError,
  } = useScanProviderSchema();

  const {
    integrations,
    loading: integrationsLoading,
    error: integrationsError,
  } = useAttachableAwsIntegrations(space);

  const providerVersionOptions = providerVersions.map((version) => ({
    value: version,
    label: version,
  }));

  const integrationOptions = integrations.map(({ id, name }) => ({ value: id, label: name }));

  const providerVersionHelperText = schemaError ? "Could not load provider versions." : undefined;
  const regionsHelperText = schemaError ? "Could not load regions." : undefined;
  const integrationHelperText = integrationsError ? "Could not load integrations." : undefined;

  return (
    <FullScreenModalBody>
      <FullScreenModalBodyHeader
        {...SCHEDULE_SCAN_WIZARD_STEP_HEADERS[NewScanScheduleStep.AttachProviders]}
      />

      <Box direction="column" fullWidth>
        <TileCheckboxGrid columns={4}>
          {PROVIDERS.map((provider) => (
            <ProviderTile key={provider.name} {...provider} />
          ))}
        </TileCheckboxGrid>

        <Box direction="column" margin="x-large 0 0">
          <Controller
            name="providerVersion"
            control={control}
            rules={{ required: "Provider version is required." }}
            render={({ field }) => (
              <ComboBox
                label="Provider version"
                tooltipInfo={tooltips.PROVIDER_VERSION}
                error={errors?.providerVersion?.message}
                helperText={providerVersionHelperText}
                value={field.value}
                items={providerVersionOptions}
                isLoading={schemaLoading}
                onChange={(value) => field.onChange(value ?? "")}
                placeholder="Select a provider version"
              >
                {(item) => <ComboBoxDefaultItem id={item.value} {...item} />}
              </ComboBox>
            )}
          />

          <Controller
            name="integrationId"
            control={control}
            rules={{ required: "Integration is required." }}
            render={({ field }) => (
              <FormField
                label="Integration"
                tooltipInfo={tooltips.INTEGRATION}
                tooltipInfoVariant="modal"
                error={errors?.integrationId?.message}
                helperText={integrationHelperText}
              >
                {({ ariaInputProps }) => (
                  <Select
                    value={field.value}
                    items={integrationOptions}
                    isLoading={integrationsLoading}
                    onChange={field.onChange}
                    placeholder="Please select"
                    isError={!!errors?.integrationId}
                    {...ariaInputProps}
                  />
                )}
              </FormField>
            )}
          />

          <Controller
            name="regions"
            control={control}
            rules={{
              validate: (value) => value.length > 0 || "Select at least one region.",
            }}
            render={({ field }) => (
              <RegionsField
                value={field.value}
                onChange={field.onChange}
                options={regions}
                isLoading={schemaLoading}
                error={errors?.regions?.message}
                helperText={regionsHelperText}
              />
            )}
          />
        </Box>
      </Box>
    </FullScreenModalBody>
  );
};

export default NewScanScheduleStepAttachProviders;
